import { useRef, useState, useEffect } from 'react';
import { API } from 'aws-amplify';
import { Alert, Spinner, Button, Row, Col } from "react-bootstrap";
import BaseCompensationDataInputs from '../ExploreCompensationDataPage/BaseCompensationDataInputs/BaseCompensationDataInputs';
import { getAuthToken } from '../utils/AuthUtils';
import './styles.scss';
import CompensationGrid from '../CommonComponents/CompensationGrid/CompensationGrid';

const OptionsData = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [hasResults, setHasResults] = useState(false);
    const [errorDisplayStatus, setErrorDisplayStatus] = useState(false);
    const [error, setError] = useState("");
    const [gridExpansionStatus, setGridExpansionStatus] = useState(false);
    const [selectedDataRows, setSelectedDataRows] = useState(new Set());
    const [noDataFound, setNoDataFound] = useState([]);
    const formRef = useRef(null);
    const [companyIds, setCompanyIds] = useState([]);

    const columns = [{
        "key": "ticker",
        "name": "Ticker",
        "dataType": "STRING",
        "sortable": true,
        "filterable": true
    },
    {
        "key": "companyName",
        "name": "Company Name",
        "dataType": "STRING",
        "sortable": true,
        "filterable": true
    },
    {
        "id": "ipoDate",
        "key": "ipoDate",
        "name": "IPO Date",
        "dataType": "STRING",
        "sortable": true,
        "filterable": true
    },
    {
        "key": "fye",
        "name": "FYE",
        "dataType": "DATE",
        "sortable": true,
        "filterable": true
    },
    {
        "id": "basicWeight",
        "key": "basicWeight",
        "name": "Basic Weight",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "totalOutstandingBsData",
        "key": "totalOutstandingBsData",
        "name": "Total Outstanding BS Data",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "totalOptionsGranted",
        "key": "totalOptionsGranted",
        "name": "Total Options Granted",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optionsStrikePriceGranted",
        "key": "optionsStrikePriceGranted",
        "name": "Options Strike Price Granted",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "totalOptionsCancelled",
        "key": "totalOptionsCancelled",
        "name": "Total Options Cancelled",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optionsStrikePriceCancelled",
        "key": "optionsStrikePriceCancelled",
        "name": "Options Strike Price Cancelled",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optionsEndOs",
        "key": "optionsEndOs",
        "name": "Options Ends OS",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optionsStrikePriceOs",
        "key": "optionsStrikePriceOs",
        "name": "Options Strike Price OS",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optFairValueLow",
        "key": "optFairValueLow",
        "name": "Opt Fair Value Low",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optFairValueHigh",
        "key": "optFairValueHigh",
        "name": "Opt Fair Value High",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optLifeLow",
        "key": "optLifeLow",
        "name": "Opt Life Low",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optLifeHigh",
        "key": "optLifeHigh",
        "name": "Opt Life High",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optVolLow",
        "key": "optVolLow",
        "name": "Opt Vol Low",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optVolHigh",
        "key": "optVolHigh",
        "name": "Opt Vol High",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optDivYldLow",
        "key": "optDivYldLow",
        "name": "Opt Div Yield Low",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optDivYldHigh",
        "key": "optDivYldHigh",
        "name": "Opt Div Yield High",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optRiskFreeLow",
        "key": "optRiskFreeLow",
        "name": "Opt Risk Free Low",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    {
        "id": "optRiskFreeHigh",
        "key": "optRiskFreeHigh",
        "name": "Opt Risk Free High",
        "dataType": "NUMBER",
        "sortable": true,
        "filterable": false
    },
    ];

    const handleBringSelectedDataToTop = (data) => {
        if (selectedDataRows.size > 0) {
            const selectedRows = [...selectedDataRows];
            const selectedData = data.filter(datum => selectedRows.includes(datum.uniqueId));
            const unselectedData = data.filter(datum => !selectedRows.includes(datum.uniqueId));
            const sortedData = [...selectedData, ...unselectedData];
            setData(sortedData);
            setSelectedDataRows(new Set(selectedRows))
        } else {
            setData(data);
        }
    }

    const handleSelectedRowsChange = (selectedRowIds) => {
        setSelectedDataRows(selectedRowIds);
    }

    const handleResponse = (response) => {
        const tempDataArr = [];
        const noData = [];

        for (const comp of response) {
            const ticker = comp.ticker;
            const exchange = comp.exchange;
            const companyName = comp.companyName;
            const ipoDate = comp.ipoDate;

            if (!comp.basicWeight || comp.basicWeight.length === 0) {
                noData.push(ticker);
                continue;
            }

            for (let i = 0; i < 3; i++) {
                const data = {};
                data["ticker"] = ticker;
                data["exchange"] = exchange;
                data["company_name"] = companyName;
                data["ipoDate"] = ipoDate;
                data["fye"] = comp.basicWeight[i].fye;
                data["basicWeight"] = comp.basicWeight[i].value;
                data["totalOutstandingBsData"] = comp.totalOutstandingBsData[i].value;
                data["totalOptionsGranted"] = comp.totalOptionsGranted[i].value;
                data["optionsStrikePriceGranted"] = comp.optionsStrikePriceGranted[i].value;
                data["totalOptionsCancelled"] = comp.totalOptionsCancelled[i].value;
                data["optionsStrikePriceCancelled"] = comp.optionsStrikePriceCancelled[i].value;
                data["optionsEndOs"] = comp.optionsEndOs[i].value;
                data["optionsStrikePriceOs"] = comp.optionsStrikePriceOs[i].value;
                data["optFairValueLow"] = comp.optFairValueLow[i].value;
                data["optFairValueHigh"] = comp.optFairValueHigh[i].value;
                data["optLifeLow"] = comp.optLifeLow[i].value;
                data["optLifeHigh"] = comp.optLifeHigh[i].value;
                data["optVolLow"] = comp.optVolLow[i].value;
                data["optVolHigh"] = comp.optVolHigh[i].value;
                data["optDivYldLow"] = comp.optDivYldLow[i].value;
                data["optDivYldHigh"] = comp.optDivYldHigh[i].value;
                data["optRiskFreeLow"] = comp.optRiskFreeLow[i].value;
                data["optRiskFreeHigh"] = comp.optRiskFreeHigh[i].value;
                data["uniqueId"] = ticker + "#" + i;

                tempDataArr.push(data);
            }
        }
        setNoDataFound(noData);
        setData(tempDataArr);
    }

    const handleSearch = async () => {
        if (companyIds.length === 0) {
            setErrorDisplayStatus(true);
            setError("Please enter Tickers");
            return;
        }

        setLoading(true);
        setErrorDisplayStatus(false);
        setError("");
        setHasResults(false);
        setNoDataFound([]);

        const queryStringParameters = {
            companyIds: companyIds.join(",")
        }

        const authToken = await getAuthToken();
        API.get("AnnualCompensations",
            "/cap-iq/options",
            {
                headers: {
                    'Token': authToken
                },
                queryStringParameters
            }).then(response => {
                handleResponse(response);
                setLoading(false);
                setHasResults(true);
            }).catch(error => {
                console.log(error)
                setError("Error retrieving options data");
                setErrorDisplayStatus(true);
                setLoading(false);
            });
    }


    const handleParsingTickers = (tickers) => {
        if (!tickers || tickers.length === 0 || tickers === "") {
            return [];
        }

        if (Array.isArray(tickers)) {
            return handleArrayTickers(tickers);
        }

        if (typeof tickers === 'string' && tickers.includes(",")) {
            return handleStringTickers(tickers);
        }

        return [tickers.toUpperCase()];
    }

    const parseTickers = (companyIds) => {
        const tickerDelimiter = /\r?\n/;
        const formattedTickers = new Set();
        companyIds.split(tickerDelimiter).forEach(tks => {
            tks.split(',').forEach(ticker => {
                formattedTickers.add(ticker.trim().toUpperCase());
            });
        });
        return formattedTickers;
    }

    const formatTickers = (companyIds) => {
        if (companyIds !== null && companyIds.length > 0) {
            if (Array.isArray(companyIds))
                return companyIds.filter(ticker => ticker !== "," || ticker !== "" || ticker !== " ");
            const formattedTickers = parseTickers(companyIds);
            return [...formattedTickers].map(t => t.toString().trim()).filter(t => t !== "" && t !== ",");
        }
    };
    const handleArrayTickers = (tickers) => {
        const uniqueTickers = [...new Set(tickers)];
        const formattedTickers = uniqueTickers.map(ticker => formatTickers(ticker));
        if (formattedTickers && formattedTickers.length > 0) {
            const flattenedTickers = [...new Set(formattedTickers.flat())]
            return flattenedTickers;
        }
        return [];
    }

    const handleStringTickers = (tickers) => {
        const rawTickers = tickers.split(",").map(ticker => ticker.trim().toUpperCase());
        const uniqueTickers = [...new Set(rawTickers)];
        const formattedTickers = uniqueTickers.map(ticker => formatTickers(ticker));
        if (formattedTickers && formattedTickers.length > 0) {
            return formattedTickers;
        }
        return [];
    }

    const handleSearchBulkTickers = async (unParsedTickers) => {
        if (formRef.current !== null) {
            formRef.current.reportValidity();
            formRef.current.classList.add("was-validated");
        }
        if (unParsedTickers === undefined || unParsedTickers === null || unParsedTickers === "" || unParsedTickers.length === 0) {
            handleParsingTickers([]);
            return;
        }
        if (formRef.current !== null) {
            formRef.current.classList.remove("was-validated");
        }
        const parsedTickers = handleParsingTickers(unParsedTickers);
        setCompanyIds(parsedTickers);
        await handleSearch()
    }

    useEffect(() => {
        const mounted = true;
        if (mounted) {
            setLoading(false);
            setData([]);
            setHasResults(false);
            setErrorDisplayStatus(false);
            setError("");
            setGridExpansionStatus(false);
            setSelectedDataRows(new Set());
            setNoDataFound([]);
            setCompanyIds([]);
        }
    }, [])

    return (
        <>
            <div className='options-data-container'>
                <Row>
                    <BaseCompensationDataInputs
                        tickers={companyIds}
                        selectedTemplateId={"TemplateId"}
                        areTickersRequired={true}
                        handleSettingTickers={(tickers) => {
                            const parsedTickers = handleParsingTickers(tickers)
                            setCompanyIds(parsedTickers);
                        }}
                        handleSearch={handleSearchBulkTickers}
                        searching={loading}
                        years={3}
                        setYears={() => { }}>
                    </BaseCompensationDataInputs>
                </Row>
                <Row>
                    <Col md={4}>
                        {loading ? <Button variant="primary toolbar-btn" disabled>
                            <outout>Searching...</outout>
                            <Spinner as="span" animation="grow" size="sm" aria-hidden="true" />
                        </Button> : <Button variant="primary toolbar-btn" onClick={handleSearch}>
                            Search
                        </Button>}
                    </Col>
                </Row>
                <div className="company-details-grid">
                    {errorDisplayStatus && <Alert variant="danger" dismissible onClose={() => setErrorDisplayStatus(false)}>{error}</Alert>}
                    {noDataFound?.length > 0 && <Alert variant="warning" dismissible >No data found for: {noDataFound.join(",")}</Alert>}
                    {loading && <Spinner animation="border" variant="primary" />}
                    {hasResults && <CompensationGrid
                        columnDefinitions={columns}
                        compensations={data}
                        rowKeyGetter={(row) => row.uniqueId}
                        selectedRows={selectedDataRows}
                        onSelectedRowsChange={(selectedRows) => handleSelectedRowsChange(selectedRows)}
                        templateTitle={"UD FVT Options Data"}
                        allowGridToExcel={true}
                        isGridExpanded={gridExpansionStatus}
                        handleGridResize={(isExpanded) => setGridExpansionStatus(isExpanded)}
                        onBringToTop={() => handleBringSelectedDataToTop(data)}
                    />
                    }
                </div>
            </div>
        </>
    )
}

export default OptionsData;